import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  X, User, Phone, Mail, Building2, Briefcase, Tag, StickyNote,
  ExternalLink, MessageSquare, Star, PhoneCall,
} from 'lucide-react';
import ContactCalls from './ContactCalls';
import { contactsService } from 'api/services/contacts.service';
import { Contact } from 'types';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';

/**
 * Who the lead is, without leaving the list of leads.
 *
 * A capture already knows its contactId, and the only thing offered for it was a
 * link to that person's OTHER captures. So the one question the row provokes —
 * "who is this?" — could be answered only by leaving: losing the filters, the
 * scroll position and the place in a queue somebody is working through. A peek
 * is the right shape for a working queue; the full profile stays one click away
 * for when the answer is "I need their whole history".
 *
 * Deliberately NOT the profile. It shows identity — names, numbers, addresses,
 * tags, remarks — and no timeline. Per-channel history costs real money here
 * (calls cross two switches over SSH tunnels) and nobody opening a peek asked
 * for it. The profile already does that, properly and lazily, per tab.
 *
 * Read-only on purpose. Editing lives in ContactEditModal, which is reached from
 * the places that own the contact; a second editor behind a lead row is how two
 * forms start disagreeing about what a valid contact looks like.
 */

interface ContactPeekModalProps {
  contactId: string;
  /** Shown while loading, so the modal opens with a name rather than a spinner. */
  fallbackLabel?: string;
  /**
   * What the thing being opened IS, in the caller's own vocabulary — "Lead",
   * "Booking". The leads queue opens this per row and the row is not a
   * "contact" to the person working it; naming it after the table the data
   * comes from tells them the one thing they did not need.
   */
  subject?: string;
  onClose: () => void;
}

const ContactPeekModal: React.FC<ContactPeekModalProps> = ({
  contactId, fallbackLabel, subject = 'Contact', onClose,
}) => {
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const res = await contactsService.getContact(contactId);
      if (cancelled) return;
      if (res.success && res.data) setContact(res.data);
      else setError(res.error || 'Could not load this contact');
      setLoading(false);
    })();
    // Guards against the response of a contact the user has already closed
    // landing in state — two quick opens would otherwise show the first one's
    // details under the second one's heading.
    return () => { cancelled = true; };
  }, [contactId]);

  // Escape closes, as it does for every other modal here. Bound on the document
  // because the dialog is not focused on open — focusing it would steal the
  // caret from whatever the operator was typing in the filter bar.
  const onKey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onKey]);

  const phones = contact?.phone_numbers || [];
  const emails = contact?.email_addresses || [];
  const labels = contact?.labels || [];

  // The heading before the fetch lands. The caller already has a name or a
  // number on the row it was opened from, and showing it immediately is the
  // difference between a modal that feels instant and one that flashes.
  const heading = contact?.name?.trim() || fallbackLabel || 'Contact';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col"
        // The backdrop closes; the panel must not, or every click inside it
        // dismisses the thing being read.
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${subject} details for ${heading}`}
      >
        <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 shrink-0 text-indigo-600 dark:text-indigo-400" />
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{heading}</h2>
            </div>
            {(contact?.company || contact?.designation) && (
              <p className="mt-1 text-[12px] flex items-center gap-3 flex-wrap" style={{ color: 'var(--text-muted)' }}>
                {contact?.company && (
                  <span className="inline-flex items-center gap-1 min-w-0">
                    <Building2 className="w-3 h-3 shrink-0" />
                    <span className="truncate">{contact.company}</span>
                  </span>
                )}
                {contact?.designation && (
                  <span className="inline-flex items-center gap-1 min-w-0">
                    <Briefcase className="w-3 h-3 shrink-0" />
                    <span className="truncate">{contact.designation}</span>
                  </span>
                )}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto space-y-4">
          {loading && <LoadingSpinner />}
          {!loading && error && <ErrorMessage message={error} />}

          {!loading && !error && contact && (
            <>
              <Section icon={<Phone className="w-3 h-3" />} title="Phone numbers">
                {phones.length === 0 ? (
                  <Empty>No number on file</Empty>
                ) : (
                  <ul className="space-y-1">
                    {phones.map((p, i) => (
                      <li key={`${p.number}-${i}`} className="flex items-center gap-2 text-[12px] flex-wrap">
                        <span className="font-mono text-gray-900 dark:text-gray-100">{p.number}</span>
                        {p.is_primary && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded
                                           bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                            <Star className="w-2.5 h-2.5" /> primary
                          </span>
                        )}
                        {p.type && <Chip>{p.type}</Chip>}
                        {/* Capability, not history: these say the number can take
                            WhatsApp or RCS, never that anything was sent. */}
                        {p.whatsapp_enabled && <Chip tone="green">WhatsApp</Chip>}
                        {p.rcs_enabled && <Chip tone="blue">RCS</Chip>}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section icon={<Mail className="w-3 h-3" />} title="Email">
                {emails.length === 0 ? (
                  <Empty>No email on file</Empty>
                ) : (
                  <ul className="space-y-1">
                    {emails.map((e, i) => (
                      <li key={`${e.email}-${i}`} className="flex items-center gap-2 text-[12px] flex-wrap">
                        <span className="text-gray-900 dark:text-gray-100 break-all">{e.email}</span>
                        {e.is_primary && <Chip tone="amber">primary</Chip>}
                        {e.type && <Chip>{e.type}</Chip>}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              {labels.length > 0 && (
                <Section icon={<Tag className="w-3 h-3" />} title="Tags">
                  <div className="flex flex-wrap gap-1">
                    {labels.map((l) => <Chip key={l}>{l}</Chip>)}
                  </div>
                </Section>
              )}

              {contact.notes && (
                <Section icon={<StickyNote className="w-3 h-3" />} title="Remarks">
                  <p className="text-[12px] text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words">
                    {contact.notes}
                  </p>
                </Section>
              )}

              {/* What was actually said — BEHIND A CLICK, on purpose.
                  This modal was written with no history at all because a
                  contact's calls cross two switches over SSH tunnels, and
                  making every peek pay that was the cost it refused. Nothing
                  here fetches until somebody asks, so opening a peek still
                  costs exactly one contact read; asking is the moment the
                  reader has decided the query is worth it. */}
              <Section icon={<PhoneCall className="w-3 h-3" />} title="Conversations">
                <ContactCalls
                  contactId={contactId}
                  /* Both parties matter: an outbound call has this person as the
                     DIALLED number, so a caller-only match drops every one. */
                  phones={phones.map((p) => p.number).filter(Boolean)}
                />
              </Section>
            </>
          )}
        </div>

        {/* Present even while loading: both destinations are known from the id
            alone, and an operator who opened this to navigate should not have to
            wait for a fetch they do not care about. */}
        <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex items-center gap-4 flex-wrap">
          <Link
            to={`/user-directory/contacts/${encodeURIComponent(contactId)}`}
            onClick={onClose}
            className="text-[12px] inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            <ExternalLink className="w-3 h-3" />
            Open full profile
          </Link>
          <Link
            to={`/crm/captures/contact/${encodeURIComponent(contactId)}`}
            onClick={onClose}
            className="text-[12px] inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:underline"
            title="Every lead, booking and message captured from this caller, across all their calls"
          >
            <MessageSquare className="w-3 h-3" />
            All their leads &amp; bookings
          </Link>
        </div>
      </div>
    </div>
  );
};

const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <div>
    <h3 className="text-[11px] font-medium uppercase tracking-wide flex items-center gap-1 mb-1.5"
        style={{ color: 'var(--text-muted)' }}>
      {icon}{title}
    </h3>
    {children}
  </div>
);

const TONES: Record<string, string> = {
  gray: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
};

const Chip: React.FC<{ children: React.ReactNode; tone?: keyof typeof TONES }> = ({ children, tone = 'gray' }) => (
  <span className={`text-[10px] px-1.5 py-0.5 rounded ${TONES[tone]}`}>{children}</span>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[12px]" style={{ color: 'var(--text-muted)' }}>{children}</p>
);

export default ContactPeekModal;
